import React, { useEffect, useState } from 'react'

import { Avatar } from '@wartek-id/avatar'
import { Icon } from '@wartek-id/icon'
import { Text } from '@wartek-id/text'

import {
  VideoDescriptionShimmer,
  VideoCardShimmer,
  VideoCountShimmer,
  VideoShimmer,
} from 'app/VideoRepository/components/Preloader'
import PlaylistInfoDialog from 'app/VideoRepository/components/PlaylistInfoDialog'
import Video from 'app/VideoRepository/Video'
import VideoCard from 'app/VideoRepository/components/VideoCard'
import PageContainer from 'app/DocumentCollectionPage/components/PageContainer'
import { SocialShareDrawer } from 'components/SocialShareDrawer'
import AndroidHandler from 'utils/androidHandler'
import { capitalizeFirstLetter } from 'utils/text'

import type { PlaylistsReviewer, PlaylistVideo } from 'types/video-inspirasi'

interface DesktopPlaylistSplitViewProps {
  playlistId: string | number
  playlistName: string
  shortDescription: string
  longDescription: string
  videos: PlaylistVideo[]
  reviewer: PlaylistsReviewer
  isLoading: boolean
  isSuccess: boolean
  screenName: string
  initialVideoId?: string
}

const DesktopPlaylistSplitView = ({
  playlistId,
  playlistName,
  shortDescription,
  longDescription,
  videos,
  reviewer,
  isLoading,
  isSuccess,
  screenName,
  initialVideoId,
}: DesktopPlaylistSplitViewProps) => {
  const androidHandler = AndroidHandler()
  const [selectedVideo, setSelectedVideo] = useState<PlaylistVideo | null>(null)
  const [showInfoDialog, setShowInfoDialog] = useState(false)
  const [showShareDialog, setShowShareDialog] = useState(false)

  // Auto-select first video on load (or from initialVideoId)
  useEffect(() => {
    if (isSuccess && videos?.length > 0) {
      const video = initialVideoId
        ? videos.find((v) => v.videoId === initialVideoId)
        : videos[0]
      if (video) {
        setSelectedVideo(video)
      }
    }
  }, [isSuccess, videos, initialVideoId])

  const handleVideoSelect = (videoId: string) => {
    const video = videos?.find((v) => v.videoId === videoId)
    if (video) {
      setSelectedVideo(video)
    }
  }

  const shareUrl = `${
    typeof window !== 'undefined'
      ? window.location.origin
      : process.env.NEXT_PUBLIC_BASE_URL
  }/video-inspirasi/playlists/video?id=${playlistId}&video=${
    selectedVideo?.videoId ?? ''
  }`

  const triggerShareDialog = () => {
    if (androidHandler.enabled) {
      androidHandler.shareToolkitUrl(
        `Lihat video: ${selectedVideo?.title} di ${shareUrl}`
      )
    } else {
      setShowShareDialog(true)
    }
  }

  return (
    <>
      <div className="flex-1 min-h-0 overflow-hidden bg-surface-subdued">
        <PageContainer className="flex h-full !py-0">
          {/* Left panel: playlist info + video list */}
          <aside
            id="desktop-split-left-panel"
            className="w-[38%] min-w-0 border-r border-gray-200 overflow-y-auto flex flex-col"
          >
            {isLoading ? (
              <div className="bg-white p-4 md:px-6">
                <VideoDescriptionShimmer />
                <div className="mt-4">
                  <VideoCountShimmer />
                  {[...Array(3)].map((_, index) => (
                    <VideoCardShimmer key={index} />
                  ))}
                </div>
              </div>
            ) : (
              <>
                {/* Concise playlist info */}
                <div className="bg-white p-4 md:px-6 border-b border-gray-200">
                  <Text variant="heading-md" className="mb-1">
                    {playlistName}
                  </Text>
                  <Text
                    color="subdued"
                    variant="body-sm"
                    className="mb-2 line-clamp-2"
                  >
                    {capitalizeFirstLetter(shortDescription ?? '')}
                  </Text>
                  {reviewer && (
                    <div className="flex items-center mb-2">
                      <Avatar
                        alt=""
                        shape="circle"
                        size="sm"
                        src={reviewer.imageUrl}
                      />
                      <Text variant="body-sm" color="subdued" className="ml-2">
                        Dikurasi oleh {reviewer.name}
                      </Text>
                    </div>
                  )}
                  <button
                    onClick={() => setShowInfoDialog(true)}
                    className="flex items-center border-none bg-transparent focus:outline-none cursor-pointer p-0"
                    type="button"
                  >
                    <span className="mr-1 font-bold text-sm text-interactive-default">
                      Baca selengkapnya
                    </span>
                    <Icon color="informational" fontSize="small">
                      chevron_right
                    </Icon>
                  </button>
                </div>

                {/* Video list */}
                <div className="flex-1 bg-white">
                  <div className="px-4 md:px-6 py-3 border-b border-gray-200">
                    <Text variant="body-sm" color="subdued">
                      {videos?.length ?? 0} Video
                    </Text>
                  </div>
                  {videos?.map(
                    ({ title, videoId, duration, thumbnailUrl }, index) => (
                      <VideoCard
                        key={index}
                        playlistId={playlistId as number}
                        thumbnailUrl={thumbnailUrl}
                        duration={duration}
                        title={title}
                        videoId={videoId}
                        screenName={screenName}
                        isActive={videoId === selectedVideo?.videoId}
                        onClick={() => handleVideoSelect(videoId)}
                      />
                    )
                  )}
                </div>
              </>
            )}
          </aside>

          {/* Right panel: video player */}
          <main className="flex-1 min-w-0 overflow-y-auto bg-white">
            {isLoading && (
              <div className="p-6">
                <VideoShimmer />
              </div>
            )}
            {isSuccess && selectedVideo ? (
              <Video
                playlistId={playlistId}
                title={selectedVideo.title}
                videoId={selectedVideo.videoId}
                description={selectedVideo.description}
                duration={selectedVideo.duration}
                publisherName={selectedVideo.publisherName}
                publishedAt={selectedVideo.publishedAt}
                onShareClick={triggerShareDialog}
                isDesktop
              />
            ) : (
              isSuccess &&
              !selectedVideo && (
                <div className="flex items-center justify-center h-full">
                  <Text variant="body" color="subdued">
                    Pilih video untuk ditonton
                  </Text>
                </div>
              )
            )}
          </main>
        </PageContainer>
      </div>

      {/* Share dialog */}
      {selectedVideo && (
        <SocialShareDrawer
          isOpen={showShareDialog}
          title="Bagikan Video"
          onClose={() => setShowShareDialog(false)}
          shareText={`Lihat video: ${selectedVideo.title} `}
          shareUrl={shareUrl}
        />
      )}

      {/* Full playlist info dialog */}
      <PlaylistInfoDialog
        isOpen={showInfoDialog}
        onClose={() => setShowInfoDialog(false)}
        name={playlistName}
        shortDescription={shortDescription}
        longDescription={longDescription}
        videos={videos}
        reviewer={reviewer}
      />
    </>
  )
}

export default DesktopPlaylistSplitView
